import * as React from 'react';

import {
  LucideDownload,
  LucideLoader2,
  LucideChevronLeft,
  LucideChevronRight,
  LucideX,
} from 'lucide-react';

import {
  ensureValue,
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTrigger,
  DialogTitle,
  downloadBlob,
  openLinkInNewTab,
} from '../..';

export type PhotoPreviewDialogProps = {
  albumName?: string;
  photos?: { id: string; url?: string; name?: string }[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
};

export const PhotoPreviewDialog = ({
  albumName,
  photos,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
}: PhotoPreviewDialogProps) => {
  const currentPhoto = photos?.[currentIndex];
  const [downloadingPhoto, setDownloadingPhoto] = React.useState(false);

  const handlePrevious = React.useCallback(() => {
    if (!photos) return;
    const newIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
    onNavigate(newIndex);
  }, [currentIndex, onNavigate, photos]);

  const handleNext = React.useCallback(() => {
    if (!photos) return;
    const newIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
    onNavigate(newIndex);
  }, [currentIndex, onNavigate, photos]);

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          onClose();
          break;
      }
    },
    [isOpen, handlePrevious, handleNext, onClose],
  );

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handlePhotoDownload = async () => {
    if (!currentPhoto || downloadingPhoto) return;

    setDownloadingPhoto(true);

    try {
      const response = await fetch(ensureValue(currentPhoto.url));
      const blob = await response.blob();
      downloadBlob(blob, ensureValue(currentPhoto.name));
    } catch (error) {
      console.error('Error downloading photo:', error);
      openLinkInNewTab(
        ensureValue(currentPhoto.url),
        ensureValue(currentPhoto.name),
      );
    } finally {
      setDownloadingPhoto(false);
    }
  };

  if (!currentPhoto) return null;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onClose}>
      <DialogOverlay className={'@PhotoPreviewDialog bg-neutral/40'}>
        <DialogContent
          position="center"
          className="h-[calc(100vh-6rem)] w-[calc(100vw-6rem)]"
          closeButton={false}
        >
          <DialogHeader className="mr-0 flex flex-row items-center justify-between p-4">
            <DialogTitle className="mb-0 flex flex-col gap-[-1px] text-lg font-semibold">
              {albumName && (
                <span className="text-xs font-normal text-neutral/40">
                  {albumName}
                </span>
              )}
              <span className="text-base font-medium">{currentPhoto.name}</span>
            </DialogTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="text"
                color="neutral"
                onClick={handlePhotoDownload}
                isDisabled={downloadingPhoto}
              >
                {downloadingPhoto ? (
                  <LucideLoader2 size={16} className="animate-spin" />
                ) : (
                  <LucideDownload size={16} />
                )}
              </Button>
              <Button
                variant="text"
                onClick={onClose}
                className="text-neutral/80 hover:text-neutral/70"
              >
                <LucideX size={20} />
              </Button>
            </div>
          </DialogHeader>

          <div className="relative flex min-h-0 flex-1 items-center justify-center border-y border-neutral/10 bg-white p-4">
            <img
              src={currentPhoto.url}
              alt={currentPhoto.name}
              className="max-h-full max-w-full rounded-lg object-contain"
            />

            {photos.length > 1 && (
              <>
                <Button
                  variant="outline"
                  color="neutral"
                  onClick={handlePrevious}
                  className="absolute top-1/2 left-4 size-12 -translate-y-1/2 border-neutral/20 p-0"
                >
                  <LucideChevronLeft size={24} />
                </Button>

                <Button
                  variant="outline"
                  color="neutral"
                  onClick={handleNext}
                  className="absolute top-1/2 right-4 size-12 -translate-y-1/2 border-neutral/20 p-0"
                >
                  <LucideChevronRight size={24} />
                </Button>
              </>
            )}
          </div>

          {photos.length > 1 && (
            <div className="flex items-center justify-center gap-1 p-4">
              <span className="text-sm">
                {currentIndex + 1} of {photos.length}
              </span>
            </div>
          )}
        </DialogContent>
      </DialogOverlay>
    </DialogTrigger>
  );
};
